import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { LuEye, LuEyeOff, LuLoaderCircle, LuCircleAlert } from 'react-icons/lu';
import AuthShell from '../components/AuthShell';
import BotaoGoogle from '../components/BotaoGoogle';
import Captcha, { type CaptchaHandle } from '../components/Captcha';
import { cabecalhoCaptcha, captchaAtivo } from '../lib/captcha';
import { destinoAposEntrar } from '../config/boasVindas';
import { apiFetch } from '../services/api';
import { carregarUsuarioLogado, type CadastroGoogle } from '../lib/entrarComGoogle';
import { carregarPixelMeta, contaCriadaNaMeta } from '../lib/metaPixel';

/**
 * /cadastro — Direção A do desenho fechado em 2026-09-13.
 * docs/designsystem/propostas/cadastro.html
 *
 * Quatro campos e um botão. O que saiu, e por quê:
 *
 * - CNPJ: é lido em um único lugar do sistema (CriarCliente, no checkout).
 *   Era o campo mais caro da tela — o único que ninguém sabe de cabeça — e
 *   ficava dentro da janela de 5 minutos do código, que expirava enquanto a
 *   pessoa procurava o contrato social.
 * - Código por e-mail (OTP): obrigava a sair do produto no meio do cadastro,
 *   antes de existir conta. E, quando expirava, não havia caminho de volta:
 *   o campo ficava travado com o código velho e só o F5 resolvia, apagando os
 *   sete campos. Agora a conta nasce na hora e o e-mail é confirmado depois,
 *   por link, cobrado na faixa da Home.
 * - Confirmar senha: não ia para lugar nenhum; o olho de mostrar senha cobre
 *   o mesmo risco.
 *
 * E, ao terminar, a pessoa ENTRA — antes ela era mandada para o /login digitar
 * de novo a senha que tinha acabado de criar.
 */

type CampoComErro = 'nome' | 'email' | 'senha' | 'telefone' | 'empresa';

/* Celular (16/10/2026): mesma máscara da página de captura. A API normaliza para
   o formato do WhatsApp (55 + DDD + número). */
const formatarTelefone = (cru: string): string => {
  const d = cru.replace(/\D/g, '').slice(0, 11);
  if (d.length > 10) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length > 6) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  if (d.length > 2) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length > 0) return `(${d}`;
  return '';
};

/* De onde a pessoa veio (09/10/2026): o site brikcrm.com.br manda as UTMs no
   link de cadastro. Guardamos na sessão porque o "Continuar com o Google" sai
   da página e volta sem a query. A API põe a origem no aviso de "Novo cadastro"
   da jornada. Só UTM: nada de dado pessoal (o tópico do ntfy é público).
   Desde 17/10 o site junta `fonte` (por onde a pessoa chegou: Google, Instagram…)
   e `entrada` (a primeira página que abriu), que vão marcados com "de:" e
   "entrou:" para Sistema › Site separar do botão (C:/Git/brik/src/lib/chegada.ts). */
const CHAVE_ORIGEM = 'brik:origem-cadastro';
const lerOrigem = (search: string): string | null => {
  const p = new URLSearchParams(search);
  const partes = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content']
    .map((k) => p.get(k)?.trim())
    .filter((v): v is string => Boolean(v));
  const fonte = p.get('fonte')?.replace(/·/g, ' ').trim().slice(0, 30);
  const entrada = p.get('entrada')?.replace(/·/g, ' ').trim().slice(0, 80);
  if (fonte) partes.push(`de: ${fonte}`);
  if (entrada) partes.push(`entrou: ${entrada}`);
  if (partes.length > 0) {
    const origem = partes.join(' · ').slice(0, 160);
    try { sessionStorage.setItem(CHAVE_ORIGEM, origem); } catch { /* sessão indisponível */ }
    return origem;
  }
  try { return sessionStorage.getItem(CHAVE_ORIGEM); } catch { return null; }
};

const OnboardingPage = () => {
  const apiUrl = import.meta.env.VITE_API_URL;
  const endpointLogin = import.meta.env.VITE_ENDPOINT_LOGIN;
  const navigate = useNavigate();

  /* Pixel da Meta (21/10/2026): esta é a única tela do app que ele mede. O
     anúncio precisa saber quem chegou do site e criou a conta. */
  useEffect(() => {
    carregarPixelMeta();
  }, []);

  /* Veio do "Continuar com o Google" sem conta (03/10/2026): o Google já deu
     nome e e-mail provado, então somem e-mail e senha e fica faltando a
     empresa. O id_token vale 1 hora; vencido, a API responde 401 e a tela
     volta ao cadastro comum com o aviso. */
  const location = useLocation();
  const google = (location.state as { google?: CadastroGoogle } | null)?.google ?? null;
  const [origem] = useState(() => lerOrigem(location.search));
  const [erroGoogle, setErroGoogle] = useState<string | null>(null);

  const [nome, setNome] = useState(google?.nome ?? '');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [empresa, setEmpresa] = useState('');
  const [telefone, setTelefone] = useState('');
  /* Nasce desmarcado: pela LGPD, caixa já marcada não vale como consentimento. */
  const [aceitaMarketing, setAceitaMarketing] = useState(false);

  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [carregando, setCarregando] = useState(false);
  /* Captcha só no cadastro por e-mail: pelo Google, quem garante a pessoa é o Google. */
  const captcha = useRef<CaptchaHandle>(null);
  const [tokenCaptcha, setTokenCaptcha] = useState<string | null>(null);

  const [erros, setErros] = useState<Partial<Record<CampoComErro, string>>>({});
  const [emailJaUsado, setEmailJaUsado] = useState(false);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  /* Caso raro: a conta foi criada mas o login automático não voltou. Não
     repetimos o Register — daria "e-mail já cadastrado" e pareceria um erro. */
  const [contaCriada, setContaCriada] = useState(false);

  /* ── força da senha: mesma regra do back (Senha.ValidarComplexidade) ── */
  const temTamanho = senha.length >= 8;
  const temMaiuscula = /[A-Z]/.test(senha);
  const temMinuscula = /[a-z]/.test(senha);
  const temEspecial = /[^A-Za-z0-9]/.test(senha);
  const criteriosOk = [temTamanho, temMaiuscula, temMinuscula, temEspecial].filter(Boolean).length;
  const senhaValida = criteriosOk === 4;

  const ajudaDaSenha = (): string => {
    if (!senha) return 'Mínimo de 8 caracteres, com maiúscula, minúscula e um símbolo.';
    if (senhaValida) return 'Boa senha.';

    /* Diz o que FALTA, não o que é exigido — a lista de quatro itens sempre
       acesos em vermelho era a versão antiga. */
    const faltando: string[] = [];
    if (!temTamanho) faltando.push('8 caracteres');
    if (!temMaiuscula) faltando.push('uma maiúscula');
    if (!temMinuscula) faltando.push('uma minúscula');
    if (!temEspecial) faltando.push('um símbolo');

    const lista =
      faltando.length === 1
        ? faltando[0]
        : `${faltando.slice(0, -1).join(', ')} e ${faltando[faltando.length - 1]}`;

    return `Falta ${lista}.`;
  };

  const validar = (): Partial<Record<CampoComErro, string>> => {
    const novos: Partial<Record<CampoComErro, string>> = {};
    if (!nome.trim()) novos.nome = 'Informe o seu nome.';
    if (!google) {
      if (!email.trim()) novos.email = 'Informe o seu e-mail.';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) novos.email = 'Confira o e-mail: falta algo nele.';
      if (!senha) novos.senha = 'Escolha uma senha.';
      else if (!senhaValida) novos.senha = ajudaDaSenha();
    }
    const digitosTelefone = telefone.replace(/\D/g, '').length;
    if (!digitosTelefone) novos.telefone = 'Informe o seu celular.';
    else if (digitosTelefone < 10) novos.telefone = 'Confira o celular: falta o DDD ou algum número.';
    if (!empresa.trim()) novos.empresa = 'Informe o nome da empresa.';
    return novos;
  };

  const focarPrimeiroErro = (novos: Partial<Record<CampoComErro, string>>) => {
    const ordem: CampoComErro[] = ['nome', 'email', 'senha', 'telefone', 'empresa'];
    const primeiro = ordem.find(c => novos[c]);
    if (primeiro) document.getElementById(`cad-${primeiro}`)?.focus();
  };

  /** Entra no produto com as credenciais que a pessoa acabou de criar. */
  const entrar = async (): Promise<boolean> => {
    /* Desde 04/11/2026 o Register já abre a sessão (o captcha não deixaria um segundo envio
       com o mesmo token). O Login fica só de reserva, para a API antiga ou se a sessão não abriu. */
    let me = await fetch(`${apiUrl}/api/Auth/Me`, { credentials: 'include' });
    if (!me.ok) {
      const login = await fetch(`${apiUrl}${endpointLogin}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...cabecalhoCaptcha(tokenCaptcha) },
        body: JSON.stringify({ email: email.trim(), password: senha }),
      });
      if (!login.ok) return false;
      me = await fetch(`${apiUrl}/api/Auth/Me`, { credentials: 'include' });
    }
    if (!me.ok) return false;

    localStorage.setItem('user', JSON.stringify(await me.json()));
    return true;
  };

  /** Sai do modo Google e volta ao cadastro comum (o state da rota é o que liga o modo). */
  const sairDoGoogle = () => navigate('/cadastro', { replace: true, state: null });

  /** Cadastro pelo Google: a conta nasce e a sessão abre na mesma chamada. */
  const cadastrarComGoogle = async (dadosGoogle: CadastroGoogle) => {
    const resposta = await fetch(`${apiUrl}/api/Auth/Google/Cadastro`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        idToken: dadosGoogle.idToken,
        nomeUsuario: nome.trim(),
        nomeEmpresa: empresa.trim(),
        origem,
        telefone,
        aceitaMarketing,
      }),
    });
    const dados = await resposta.json().catch(() => ({}));

    if (resposta.status === 401) {
      sairDoGoogle();
      setErroGoogle(dados.message || 'O acesso pelo Google expirou. Clique em "Continuar com o Google" de novo.');
      return;
    }
    if (!resposta.ok) {
      setErroGeral(dados.message || 'Não foi possível criar a conta.');
      return;
    }

    contaCriadaNaMeta('google');

    if (await carregarUsuarioLogado()) {
      navigate(destinoAposEntrar());
    } else {
      setErroGeral('Sua conta foi criada, mas não conseguimos entrar. Use "Continuar com o Google" no login.');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (carregando) return;

    setErroGeral(null);

    /* O botão nunca nasce desabilitado: clicar com campo faltando marca o
       campo e explica, em vez de deixar a pessoa adivinhando o que trava. */
    const novos = validar();
    setErros(novos);
    if (Object.keys(novos).length > 0) {
      focarPrimeiroErro(novos);
      return;
    }

    setCarregando(true);

    if (google) {
      try {
        await cadastrarComGoogle(google);
      } catch {
        setErroGeral('Não conseguimos falar com o servidor. Verifique a conexão e tente de novo.');
      } finally {
        setCarregando(false);
      }
      return;
    }

    if (!contaCriada && captchaAtivo && !tokenCaptcha) {
      setErroGeral('Aguarde a verificação de segurança terminar e tente de novo.');
      setCarregando(false);
      return;
    }

    try {
      if (!contaCriada) {
        const resposta = await apiFetch(`${apiUrl}/api/Auth/Register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...cabecalhoCaptcha(tokenCaptcha) },
          body: JSON.stringify({
            nomeUsuario: nome.trim(),
            email: email.trim(),
            senha,
            nomeEmpresa: empresa.trim(),
            origem,
            telefone,
            aceitaMarketing,
          }),
        });

        const dados = await resposta.json().catch(() => ({}));

        if (!resposta.ok || dados.sucesso === false) {
          const mensagem: string = dados.mensagem || dados.message || 'Não foi possível criar a conta.';
          captcha.current?.reiniciar(); // o token já foi gasto nesta tentativa

          if (mensagem.toLowerCase().includes('celular')) {
            setErros({ telefone: mensagem });
            document.getElementById('cad-telefone')?.focus();
          } else if (mensagem.toLowerCase().includes('e-mail')) {
            setEmailJaUsado(mensagem.toLowerCase().includes('já existe'));
            setErros({ email: mensagem });
            document.getElementById('cad-email')?.focus();
          } else {
            setErroGeral(mensagem);
          }
          return;
        }

        setContaCriada(true);
        contaCriadaNaMeta('formulario');
      }

      /* Conta nova entra pelo guia de boas-vindas (28/10/2026); a API é quem diz. */
      if (await entrar()) {
        navigate(destinoAposEntrar());
      } else {
        setErroGeral('Sua conta foi criada, mas não conseguimos entrar automaticamente.');
      }
    } catch {
      setErroGeral('Não conseguimos falar com o servidor. Verifique a conexão e tente de novo.');
    } finally {
      setCarregando(false);
    }
  };

  if (contaCriada && erroGeral) {
    return (
      <AuthShell>
        <h1 className="au-h1">Conta criada!</h1>
        <p className="au-sub">
          Só não conseguimos entrar automaticamente. Use o seu e-mail e a senha que você acabou de
          escolher.
        </p>
        <Link to="/login" className="au-btn" style={{ textDecoration: 'none' }}>
          Entrar
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="au-h1">{google ? 'Falta só a sua empresa' : 'Criar sua conta'}</h1>
      <p className="au-sub">
        {google
          ? 'Você vai entrar com a sua conta Google, sem senha.'
          : 'Leva menos de um minuto. Depois disso você já está dentro.'}
      </p>

      {!google && (
        <>
          <BotaoGoogle onErro={setErroGoogle} />
          {erroGoogle && (
            <div className="au-alert is-danger" role="alert">
              <LuCircleAlert size={16} />
              <span>{erroGoogle}</span>
            </div>
          )}
          <div className="au-ou" aria-hidden="true">ou com e-mail</div>
        </>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {erroGeral && (
          <div className="au-alert is-danger" role="alert">
            <LuCircleAlert size={16} />
            <span>{erroGeral}</span>
          </div>
        )}

        <div className="au-group">
          <label className="au-label" htmlFor="cad-nome">Seu nome</label>
          <div className="au-input-wrap">
            <input
              id="cad-nome"
              className={`au-input${erros.nome ? ' is-error' : ''}`}
              type="text"
              placeholder="João da Silva"
              value={nome}
              autoComplete="name"
              /* Pelo Google o nome já vem preenchido: o foco vai para a empresa. */
              autoFocus={!google?.nome}
              onChange={e => { setNome(e.target.value); if (erros.nome) setErros(p => ({ ...p, nome: undefined })); }}
              aria-invalid={!!erros.nome}
            />
          </div>
          {erros.nome && (
            <p className="au-error" role="alert"><LuCircleAlert size={13} /><span>{erros.nome}</span></p>
          )}
        </div>

        {google && (
          <div className="au-group">
            <span className="au-label">E-mail</span>
            <div className="au-conta-google">
              <span title={google.email}>{google.email}</span>
              <button type="button" onClick={sairDoGoogle}>Usar outro e-mail</button>
            </div>
            <p className="au-help">Confirmado pelo Google. É com ele que você vai entrar.</p>
          </div>
        )}

        {!google && (<>
        <div className="au-group">
          <label className="au-label" htmlFor="cad-email">E-mail</label>
          <div className="au-input-wrap">
            <input
              id="cad-email"
              className={`au-input${erros.email ? ' is-error' : ''}`}
              type="email"
              inputMode="email"
              placeholder="voce@suaempresa.com.br"
              value={email}
              autoComplete="email"
              onChange={e => {
                setEmail(e.target.value);
                setEmailJaUsado(false);
                if (erros.email) setErros(p => ({ ...p, email: undefined }));
              }}
              aria-invalid={!!erros.email}
            />
          </div>
          {erros.email ? (
            <p className="au-error" role="alert">
              <LuCircleAlert size={13} />
              {/* O erro traz a saída junto: antes ele vinha num toast que sumia
                  em três segundos e não dizia o que fazer. */}
              <span>
                {erros.email}{' '}
                {emailJaUsado && <><Link to="/login">Entrar</Link> ou <Link to="/login">recuperar a senha</Link>.</>}
              </span>
            </p>
          ) : (
            <p className="au-help">É com ele que você vai entrar. Usamos só para isso.</p>
          )}
        </div>

        <div className="au-group">
          <label className="au-label" htmlFor="cad-senha">Senha</label>
          <div className="au-input-wrap has-toggle">
            <input
              id="cad-senha"
              className={`au-input${erros.senha ? ' is-error' : ''}`}
              type={mostrarSenha ? 'text' : 'password'}
              placeholder="••••••••"
              value={senha}
              autoComplete="new-password"
              onChange={e => { setSenha(e.target.value); if (erros.senha) setErros(p => ({ ...p, senha: undefined })); }}
              aria-invalid={!!erros.senha}
            />
            <button
              type="button"
              className="au-toggle"
              onClick={() => setMostrarSenha(v => !v)}
              aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {mostrarSenha ? <LuEyeOff size={16} /> : <LuEye size={16} />}
            </button>
          </div>
          <div className="au-meter" aria-hidden="true">
            {[0, 1, 2, 3].map(i => (
              <span
                key={i}
                className={
                  criteriosOk > i ? (criteriosOk === 1 && senha ? 'is-weak' : 'is-on') : ''
                }
              />
            ))}
          </div>
          <p className={erros.senha ? 'au-error' : 'au-help'} role={erros.senha ? 'alert' : undefined}>
            {erros.senha ? <><LuCircleAlert size={13} /><span>{erros.senha}</span></> : ajudaDaSenha()}
          </p>
        </div>
        </>)}

        <div className="au-group">
          <label className="au-label" htmlFor="cad-telefone">Celular (WhatsApp)</label>
          <div className="au-input-wrap">
            <input
              id="cad-telefone"
              className={`au-input${erros.telefone ? ' is-error' : ''}`}
              type="tel"
              inputMode="tel"
              placeholder="(11) 98765-4321"
              value={telefone}
              autoComplete="tel-national"
              onChange={e => { setTelefone(formatarTelefone(e.target.value)); if (erros.telefone) setErros(p => ({ ...p, telefone: undefined })); }}
              aria-invalid={!!erros.telefone}
            />
          </div>
          {erros.telefone ? (
            <p className="au-error" role="alert"><LuCircleAlert size={13} /><span>{erros.telefone}</span></p>
          ) : (
            <p className="au-help">Para a gente te ajudar a configurar a conta, se precisar.</p>
          )}
        </div>

        <div className="au-group">
          <label className="au-label" htmlFor="cad-empresa">Nome da empresa</label>
          <div className="au-input-wrap">
            <input
              id="cad-empresa"
              className={`au-input${erros.empresa ? ' is-error' : ''}`}
              type="text"
              placeholder="Padaria do João"
              value={empresa}
              autoComplete="organization"
              autoFocus={!!google?.nome}
              onChange={e => { setEmpresa(e.target.value); if (erros.empresa) setErros(p => ({ ...p, empresa: undefined })); }}
              aria-invalid={!!erros.empresa}
            />
          </div>
          {erros.empresa ? (
            <p className="au-error" role="alert"><LuCircleAlert size={13} /><span>{erros.empresa}</span></p>
          ) : (
            <p className="au-help">Aparece para o seu time. Dá para mudar depois.</p>
          )}
        </div>

        {/* Opcional e separado dos Termos: aceitar marketing não pode ser condição
            para criar a conta. Contato sobre a própria conta não depende desta caixa. */}
        <label className="au-check is-consentimento">
          <input type="checkbox" checked={aceitaMarketing} onChange={e => setAceitaMarketing(e.target.checked)} />
          <span>Quero receber dicas e novidades do Brik por WhatsApp e e-mail.</span>
        </label>

        {!google && <Captcha ref={captcha} onToken={setTokenCaptcha} />}

        <button type="submit" className={`au-btn${carregando ? ' is-loading' : ''}`}>
          {carregando ? (
            <><LuLoaderCircle size={16} className="au-spin" /> Criando sua conta…</>
          ) : (
            'Criar conta grátis'
          )}
        </button>

        <p className="au-terms">
          Ao criar a conta você aceita os <Link to="/termos">Termos de Uso</Link> e a{' '}
          <Link to="/privacidade">Política de Privacidade</Link>.
        </p>

        {/* As três provas saem de medida, não de marketing: o trial é de 15 dias
            (AssinaturaService.AtivarTrial) e o plano "Teste Grátis" diz "sem
            cartão, sem burocracia" na própria descrição. */}
        <p className="au-proof">
          <span>15 dias grátis</span>
          <span>Sem cartão de crédito</span>
          <span>Cancele quando quiser</span>
        </p>
      </form>

      <p className="au-bloco-foot">
        Já tem conta? <Link to="/login">Entrar</Link>
      </p>
    </AuthShell>
  );
};

export default OnboardingPage;
